'use client'

import { useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { X, ChevronLeft, ChevronRight } from 'lucide-react'
import './home-v2.css'

// Matches the .hv2-lightbox.is-closing transition in home-v2.css.
const CLOSE_MS = 200

/**
 * Full-screen viewer for a set of images.
 *
 * Rendered through a portal on document.body rather than in place: the
 * sections that use it sit inside stacking contexts (isolation, transforms),
 * and a `position: fixed` overlay nested inside one of those is clipped to
 * that ancestor instead of the viewport.
 *
 * `index` of null means closed. Opening, stepping and closing are CSS
 * animations (home-v2.css); the last image stays mounted for the closing fade.
 */
export default function Lightbox({ images, index, onClose, onStep }) {
  const open = index !== null && index >= 0 && images.length > 0
  const [shown, setShown] = useState(open ? index : null)
  const [closing, setClosing] = useState(false)

  // State that follows a prop, adjusted during render (React's documented
  // pattern): track the open image, and hold the last one while it fades out.
  if (open && (shown !== index || closing)) {
    setShown(index)
    setClosing(false)
  } else if (!open && shown !== null && !closing) {
    setClosing(true)
  }

  useEffect(() => {
    if (!closing) return undefined
    const id = setTimeout(() => {
      setShown(null)
      setClosing(false)
    }, CLOSE_MS)
    return () => clearTimeout(id)
  }, [closing])

  const step = useCallback(
    (delta) => {
      if (!open) return
      onStep((index + delta + images.length) % images.length)
    },
    [open, index, images.length, onStep],
  )

  useEffect(() => {
    if (!open) return undefined
    const onKey = (e) => {
      if (e.key === 'Escape') onClose()
      else if (e.key === 'ArrowRight') step(1)
      else if (e.key === 'ArrowLeft') step(-1)
    }
    window.addEventListener('keydown', onKey)
    // Hold the page still behind the overlay, and restore whatever overflow
    // the document had rather than assuming it was the default.
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = previous
    }
  }, [open, onClose, step])

  if (typeof document === 'undefined' || shown === null) return null
  const image = images[shown]

  return createPortal(
    <div
      className={`hv2-lightbox${closing ? ' is-closing' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-label="Image viewer"
      onClick={onClose}
    >
      <button type="button" className="hv2-lb-close" onClick={onClose} aria-label="Close">
        <X size={20} />
      </button>

      {images.length > 1 ? (
        <button
          type="button"
          className="hv2-lb-nav prev"
          aria-label="Previous image"
          onClick={(e) => { e.stopPropagation(); step(-1) }}
        >
          <ChevronLeft size={22} />
        </button>
      ) : null}

      {/* Keyed by source, so each new image plays the pop-in again. */}
      <img
        key={image?.src}
        className="hv2-lb-image"
        src={image?.src}
        alt={image?.alt || ''}
        onClick={(e) => e.stopPropagation()}
      />

      {images.length > 1 ? (
        <>
          <button
            type="button"
            className="hv2-lb-nav next"
            aria-label="Next image"
            onClick={(e) => { e.stopPropagation(); step(1) }}
          >
            <ChevronRight size={22} />
          </button>
          <span className="hv2-lb-count">{shown + 1} / {images.length}</span>
        </>
      ) : null}
    </div>,
    document.body,
  )
}
